import { useState, useContext } from 'react';
import { format, parseISO } from 'date-fns';
import { Close } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../../lib/dateLocale';
import {
    AppContext,
    SttChipUser,
    SttTable,
    SttTopControls,
} from '../../../sporttia/all';
import ActivityClassDialog from './ActivityClassDialog';
import useActivitiesService from '../../../services/ActivitiesService';
import DateBuilder from '../../../lib/DateBuilder';
import { PERMISSIONS } from '../../../lib/permissions';

type Props = {
    item: Activity;
    teachers?: Teacher[];
};

type Activity = {
    id: number;
    ini?: string;
    end?: string;
};

type Teacher = {
    id: number;
    name: string;
};

type ActivityClass = {
    id: number;
    created: string;
    name: string;
    teacher?: {
        name: string;
    };
    facility?: {
        id: number;
        name: string;
    };
    ini: string;
    end: string;
    status?: string;
    weekdays?: string[];
};

type ClassesQueryParams = {
    page: number;
    rows: number;
};

/**
 * ActivityClasses - 'Classes' tab in the Activity screen
 * Object item - The Activity
 * Array teachers - A list of teachers from this Sports Center
 */
export default function ActivityClasses({
    item,
    teachers = [],
}: Props): React.ReactElement {
    const queryClient = useQueryClient();

    // Selected class to display in the modal
    const [selected, setSelected] = useState<ActivityClass | null>(null);

    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [classesQueryParams, setClassesQueryParams] =
        useState<ClassesQueryParams>({
            page: 1,
            rows: 20,
        });

    const { useGetActivityClasses } = useActivitiesService();

    const classesQuery = useGetActivityClasses(item?.id, classesQueryParams);
    function deleteClasses(rows: ActivityClass[]): void {
        if (rows.length > 0) {
            cxt?.api('POST', '/batch', {
                confirmation: true,
                params: {
                    calls: rows.map((c: any) => ({
                        method: 'DELETE',
                        endpoint: `/classes/${c.id}`,
                    })),
                },
                success: () => {
                    queryClient.invalidateQueries({
                        queryKey: classesQuery.queryKey,
                    });
                },
            });
        }
    }

    return (
        <>
            <SttTopControls
                mb={2}
                mainAction={{
                    type: 'create',
                    permission: PERMISSIONS.ACTIVITY.MODIFY,
                    onClick: () =>
                        setSelected({
                            id: 0,
                            created: '',
                            name: '',
                            status: 'ACTIVE',
                            ini: item.ini
                                ? format(
                                      parseISO(item.ini),
                                      'yyyy-MM-dd 00:00:00',
                                  )
                                : format(new Date(), 'yyyy-MM-dd 00:00:00'),
                            end: item.end
                                ? format(
                                      parseISO(item.end),
                                      'yyyy-MM-dd 23:55:55',
                                  )
                                : format(new Date(), 'yyyy-MM-dd 23:55:55'),
                            weekdays: [],
                        }),
                }}
            />
            <SttTable
                data={classesQuery?.data}
                onChangePagination={(page: number) =>
                    setClassesQueryParams({ ...classesQueryParams, page })
                }
                selection={{
                    actions: [
                        {
                            caption: t('Delete'),
                            icon: <Close />,
                            onClick: deleteClasses,
                        },
                    ],
                }}
                onFetch={() => {}}
                columns={[
                    {
                        title: t('Created'),
                        name: 'created',
                        field: 'created',
                        value: (row: ActivityClass) =>
                            new DateBuilder(
                                row.created,
                                cxt?.sc?.timezone?.name,
                                cxt?.lang?.key,
                            ).dmy(),
                    },
                    {
                        title: t('Name'),
                        name: 'name',
                        field: 'name',
                        type: 'link',
                        onClick: (row: ActivityClass) => setSelected(row),
                    },
                    {
                        title: t('Teacher'),
                        name: 'teacher',
                        field: 'teacher',
                        value: (row: ActivityClass) =>
                            row.teacher ? (
                                <SttChipUser
                                    user={{ login: row.teacher.name }}
                                />
                            ) : null,
                    },
                    {
                        title: t('Facility'),
                        name: 'facility',
                        type: 'facility',
                        field: 'facility',
                    },
                    {
                        title: t('Day'),
                        name: 'day',
                        value: (row: ActivityClass) =>
                            format(parseISO(row.ini), 'EEE', {
                                locale: getDateLocale(),
                            }),
                    },
                    {
                        title: t('Date'),
                        name: 'date',
                        type: 'dmy',
                        field: 'ini',
                    },
                    {
                        title: t('Time'),
                        name: 'time',
                        type: 'periodTime',
                        value: (row: ActivityClass) => ({
                            ini: row.ini,
                            end: row.end,
                        }),
                    },
                ]}
            />
            <ActivityClassDialog
                open={selected !== null}
                item={selected as any}
                activity={item}
                onSave={() => {
                    classesQuery.refetch();
                }}
                onClose={() => setSelected(null)}
                availableTeachers={teachers}
            />
        </>
    );
}
